import type { ReactNode } from 'react'
import { Link } from 'react-router'
import type { AssessmentChanges, FindingSummary, Overview } from '../../lib/api'
import { AREA_PAGES } from '../areaPages'
import './live.css'

/**
 * What the list below can be narrowed to from the strip: findings past their target, or the quick wins.
 * Applied over the rows already loaded, like the search.
 */
export type Focus = 'pastTarget' | 'quickWins' | null

/**
 * Whole days a finding has been open, from first seen, never negative. The same count the dashboard
 * uses, kept here so this list does not import the dashboard (which imports the list).
 */
export function daysOpen(iso: string, now = Date.now()): number {
  const then = Date.parse(iso)
  return Number.isNaN(then) ? 0 : Math.max(0, Math.floor((now - then) / 86_400_000))
}

/** The target a finding has passed, in days, or null when it has not (or its severity has none). */
export function passedTarget(f: FindingSummary, targets: Record<string, number> | undefined, now = Date.now()): number | null {
  const days = targets?.[f.severity]
  return days != null && daysOpen(f.firstSeenAt, now) > days ? days : null
}

/** Critical or High and Low effort: the "Start here" rule Recommendations and the report already use. */
export const isQuickWin = (f: FindingSummary) => (f.severity === 'Critical' || f.severity === 'High') && f.effort === 'Low'

const RANKED = ['Critical', 'High', 'Medium', 'Low'] as const

/**
 * The insights strip above an area page's list, or Recommendations' (findings pages redesign, owner
 * 2026-10-11: "go with your recommendation"). Every figure is the server's, from the same snapshot as
 * the dashboard's: open by severity, past target, quick wins, and either what the last assessment
 * changed in this area or, across every area, who owns the work and where it is.
 *
 * The figures that narrow the list are buttons, pressed while they apply, so the strip and the list
 * cannot disagree about what is shown. Nothing here is counted as clean when an area was not assessed:
 * the status line above says that, and an area with nothing open shows its zero beside it.
 */
export function FindingsInsights({
  area,
  overview,
  changes,
  focus,
  onFocus,
  ownerNone,
  onOwnerNone,
  areaTile,
}: {
  area?: string
  overview: Overview
  changes: AssessmentChanges | null | 'unavailable'
  focus: Focus
  onFocus: (focus: Focus) => void
  ownerNone: boolean
  onOwnerNone: () => void
  areaTile?: ReactNode
}) {
  const split: Record<string, number> = area
    ? { ...(overview.activeByAreaAndSeverity?.[area] ?? {}) }
    : { ...overview.activeBySeverity }
  const total = area ? overview.activeByArea[area] ?? 0 : Object.values(overview.activeBySeverity).reduce((n, v) => n + v, 0)
  const insights = overview.areaInsights
  const mine = area ? insights?.[area] : undefined
  const all = insights ? Object.values(insights) : []
  const pastTarget = area ? mine?.pastTarget ?? (insights ? 0 : null) : insights ? all.reduce((n, a) => n + a.pastTarget, 0) : null
  const quickWins = area ? mine?.quickWins ?? (insights ? 0 : null) : insights ? all.reduce((n, a) => n + a.quickWins, 0) : null
  const unowned = insights ? all.reduce((n, a) => n + a.unowned, 0) : null
  const targets = overview.remediationTargetDays
  const splitTotal = RANKED.reduce((n, s) => n + (split[s] ?? 0), 0)

  const press = (next: Exclude<Focus, null>) => onFocus(focus === next ? null : next)

  return (
    <div className={area ? (areaTile ? 'fi-strip' : 'fi-strip fi-strip-4') : 'fi-strip fi-strip-all'} aria-label="Insights" role="group">
      <button type="button" className="fi-tile fi-open" aria-pressed={focus === null && !ownerNone} onClick={() => onFocus(null)}>
        <span className="fi-label">Open</span>
        <span className="fi-value">{total}</span>
        {splitTotal > 0 ? (
          <span className="fi-bar" aria-hidden="true">
            {RANKED.map((s) => (split[s] ? <i key={s} className={`dv2-seg-${s.toLowerCase()}`} style={{ width: `${(split[s]! / splitTotal) * 100}%` }} /> : null))}
          </span>
        ) : null}
        <span className="fi-sub">{RANKED.map((s) => `${split[s] ?? 0} ${s.toLowerCase()}`).join(' · ')}</span>
      </button>

      {area ? (
        <>
          <PastTargetTile value={pastTarget} focus={focus} onPress={() => press('pastTarget')}
            sub={mine?.oldestFirstSeen ? `Oldest open ${daysLabel(daysOpen(mine.oldestFirstSeen))}` : 'None open'} />
          <QuickWinsTile label="Quick wins" value={quickWins} focus={focus} onPress={() => press('quickWins')} />
          <ChangesTile area={area} changes={changes} />
          {areaTile}
        </>
      ) : (
        <>
          <QuickWinsTile label="Start here" value={quickWins} focus={focus} onPress={() => press('quickWins')} />
          <PastTargetTile value={pastTarget} focus={focus} onPress={() => press('pastTarget')}
            sub={targets ? `Critical ${targets.Critical} days, high ${targets.High}, medium ${targets.Medium}, low ${targets.Low}` : 'Not counted yet'} />
          <button type="button" className="fi-tile" aria-pressed={ownerNone} onClick={onOwnerNone} disabled={unowned === null}>
            <span className="fi-label">No owner</span>
            <span className="fi-value">{unowned ?? '—'}</span>
            <span className="fi-sub">of {total} open</span>
          </button>
          <WhereTheWorkIs overview={overview} />
        </>
      )}
    </div>
  )
}

const daysLabel = (n: number) => (n === 1 ? '1 day' : `${n} days`)

function PastTargetTile({ value, sub, focus, onPress }: { value: number | null; sub: string; focus: Focus; onPress: () => void }) {
  return (
    <button type="button" className={value ? 'fi-tile fi-warn' : 'fi-tile'} aria-pressed={focus === 'pastTarget'} onClick={onPress} disabled={value === null}>
      <span className="fi-label">Past target</span>
      <span className={value ? 'fi-value fi-bad' : 'fi-value'}>{value ?? '—'}</span>
      <span className="fi-sub">{sub}</span>
    </button>
  )
}

function QuickWinsTile({ label, value, focus, onPress }: { label: string; value: number | null; focus: Focus; onPress: () => void }) {
  return (
    <button type="button" className="fi-tile" aria-pressed={focus === 'quickWins'} onClick={onPress} disabled={value === null}>
      <span className="fi-label">{label}</span>
      <span className="fi-value">{value ?? '—'}</span>
      <span className="fi-sub">Critical or high, low effort</span>
    </button>
  )
}

/**
 * New, resolved and came back in this area, from the report's own changes read. Counted per area only
 * when every changed finding is named (up to 50 a list); past that the area's share is not known, and
 * the tile says so rather than guess.
 */
function ChangesTile({ area, changes }: { area: string; changes: AssessmentChanges | null | 'unavailable' }) {
  const body = () => {
    if (changes === 'unavailable') return <span className="fi-sub">Could not be loaded just now.</span>
    if (changes === null || changes.previous === null) return <span className="fi-sub">Appears after your next assessment.</span>
    const lists = [changes.new, changes.resolved, changes.reopened]
    if (lists.some((l) => l.items.length < l.count)) {
      return <span className="fi-sub">Too many changes to split by area here; the report lists them.</span>
    }
    const [n, r, b] = lists.map((l) => l.items.filter((i) => i.area === area).length)
    return (
      <span className="fi-changes">
        <span><b>{n}</b> new</span>
        <span><b>{r}</b> resolved</span>
        <span><b>{b}</b> came back</span>
      </span>
    )
  }
  return (
    <div className="fi-tile fi-static">
      <span className="fi-label">Since the last assessment</span>
      {body()}
    </div>
  )
}

/** Open findings by area, each a link to its page. Recommendations only. */
function WhereTheWorkIs({ overview }: { overview: Overview }) {
  const rows = Object.entries(overview.activeByArea).sort((a, b) => b[1] - a[1])
  const top = Math.max(1, ...rows.map(([, n]) => n))
  return (
    <div className="fi-tile fi-static fi-wide">
      <span className="fi-label">Where the work is</span>
      {rows.length === 0 ? (
        <span className="fi-sub">Nothing open.</span>
      ) : (
        <ul className="fi-areas">
          {rows.map(([name, n]) => (
            <li key={name}>
              {AREA_PAGES[name] ? <Link to={AREA_PAGES[name]!}>{name}</Link> : <span>{name}</span>}
              <span className="fi-areabar" aria-hidden="true"><i style={{ width: `${(n / top) * 100}%` }} /></span>
              <span className="fi-areacount">{n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
